/**
 * @fileOverview This file contains the implementation of the RecoverScreen for the forgot password process.
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

import React, { useState } from 'react';
import logo from '../media/Logos/logo-dabi-gris.png';
import "../scss/Components/_recoverscreen.scss"
import { TextField, Button, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { GlobalVariables } from '../globalVar';
import { getToast } from '../utils/toastConfig';


/**
 * RecoverScreen for the forgot password process 
 * @returns {Component} RecoverScreen component
 */
function RecoverScreen() {
    const [showEmail, setShowEmail] = useState(true);
    const [showCode, setShowCode] = useState(false);

    const [isCodeValid, setIsCodeValid] = useState(false);
    const [showNewPasswordFields, setShowNewPasswordFields] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [email, setEmail] = useState("");

    /**
     * Handles the submission of the email form to initiate the forgot password process
     * @param {Event} event - The event object representing the form submission
     */
    function handleSendEmail(event) {
        event.preventDefault();
        axios.post(`${GlobalVariables.getURL()}forgotPassword`, {
            email: email,
        })
        .then((response) => {
            // Successful response handling
            setShowEmail(false);
            setShowCode(true);
            // Displays a success toast alert for successful email code sending
            getToast({ status: 'success', title: 'Se ha enviado el código al correo proporcionado de manera exitosa. Ingresa tu código.' })
        }, (error) => {
            // Error response handling
            console.log(error);
            // Displays an error toast alert for email code sending failure
            getToast({ status: 'error', title: 'Error al enviar código al correo proporcionado. Vuelve a intentarlo.' })
        });
    }

    const [code, setCode] = useState("");

    /**
     * Handles the validation of a reset token by making a POST request to the 'verifyToken' endpoint
     * @param {Event} event - The event object representing the form submission
     */
    function handleCodeValidation(event) {
        event.preventDefault();
        axios.post(`${GlobalVariables.getURL()}verifyToken`, {
            resetToken: code,
        })
        .then((response) => {
            // Successful response handling
            setIsCodeValid(true);
            setShowNewPasswordFields(true);
            // Displays a success toast alert for code validation
            getToast({ status: 'success', title: 'El código ha sido validado exitosamente. Ingresa tu nueva contraseña.' });
        }, (error) => {
            // Error response handling
            console.log(error);
            // Displays an error toast alert for code validation
            getToast({ status: 'error', title: 'Error al validar código. Vuelve a intentarlo.' });
        });
    };

    /**
     * Handles the submission of a new password and reset token to reset the user's password
     * @param {Event} event - The event object representing the form submission
     */
    function handleChangePassword(event) {
        event.preventDefault();
        if (newPassword === confirmPassword) {
            axios.patch(`${GlobalVariables.getURL()}resetPassword`, {
                password: newPassword,
                resetToken: code,
            })
                .then((response) => {
                    // Displays a success toast alert for successful password change
                    getToast({ status: 'success', title: 'Se ha cambiado la contraseña de manera exitosa. Prueba a iniciar sesión.' });
                }, (error) => {
                    console.log(error);
                    // Displays an error toast alert for password change failure
                    getToast({ status: 'error', title: 'Error al cambiar la contraseña. Vuelve a intentarlo.' });
                });
        } else {
            // Displays an error toast alert for mismatched passwords during password change
            getToast({ status: 'error', title: 'Contraseñas no coinciden. Vuelve a intentarlo.' })
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleYouHaveCode = () => {
        setShowEmail(false);
        setShowCode(true);
    }

    return (
        <div className="recover-screen">
            <ToastContainer />
            <img src={logo} alt="Logo de DABI" className="logo" />
            <h1 className='h1-recover-screen'>Restablecer tu contraseña</h1>

            {showEmail && (
                <p className='p-recover-screen'>Ingresa tu correo para enviarte tu código de restablecimiento de contraseña.</p>
            )}

            {showEmail && (
                <a className='other-a-recover-screen' onClick={handleYouHaveCode}>¿Ya cuentas con tu código?</a>
            )}

            {showCode && (
                <p className='p-recover-screen'>Ingresa tu código para validarlo y poder ingresar tu nueva contraseña.</p>
            )}

            {showEmail && (
                <form id="emailForm" onSubmit={handleSendEmail} style={{ width: "100%", textAlign: "center" }}>
                    <TextField
                        label="Correo"
                        variant="outlined"
                        sx={{ width: "20%", marginBottom: '20px' }}
                        type='email'
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </form>
            )}

            {showCode && (
                <form id="codeForm" onSubmit={handleCodeValidation} style={{ width: "100%", textAlign: "center" }}>
                    <TextField
                        label="Código"
                        variant="outlined"
                        disabled={isCodeValid}
                        value={code} onChange={(e) => setCode(e.target.value)}
                        InputProps={{
                            endAdornment: isCodeValid && <FontAwesomeIcon icon={faCheck} className='check-icon' />,
                        }}
                        sx={{ width: "20%", marginBottom: '20px' }}
                        required
                    />
                </form>
            )}

            {isCodeValid ? (
                <>
                    <form id="pwForm" onSubmit={handleChangePassword} style={{ width: "100%", textAlign: "center" }}>
                        <TextField
                            label="Nueva contraseña"
                            variant="outlined"
                            type={showPassword ? 'text' : 'password'}
                            margin="normal"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <IconButton onClick={handleTogglePasswordVisibility}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ),
                            }}
                            sx={{ width: '30%' }}
                            required
                        />
                        <br />
                        <TextField
                            label="Confirmar nueva contraseña"
                            variant="outlined"
                            type={showPassword ? 'text' : 'password'}
                            margin="normal"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <IconButton onClick={handleTogglePasswordVisibility}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ),
                            }}
                            sx={{ width: '30%' }}
                            required
                        />
                        <br />
                        <Button
                            variant="contained"
                            className="apply-change-button"
                            type='submit'
                        >
                            Aplicar cambio
                        </Button>
                    </form>
                </>
            ) : (
                <>
                    {showEmail && (
                        <Button
                            variant="contained"
                            type='submit'
                            className="validate-code-button"
                            form="emailForm"
                        >
                            Enviar petición
                        </Button>
                    )}

                    {showCode && (
                        <Button
                            variant="contained"
                            type='submit'
                            className="validate-code-button"
                            form="codeForm"
                        >
                            Validar código
                        </Button>
                    )}
                </>
            )}

            <a className='a-recover-screen' href="/">Volver al inicio de sesión</a>
        </div>
    );
}

export default RecoverScreen;